import _ from 'underscore';
import {
  Campaign,
  AdGroup,
  AssetGroup,
  SearchTerm,
  Keyword,
} from '@bingads-webui-campaign/campaign-ui-schemas';
import { FilterEntity } from '@bingads-webui/filter-entity';
import { WaterfallFilterType, FilterStorageFactory } from '@bingads-webui-campaign/filter-storage';
import { flattenEntitySchema, buildNameToPropertyMapping } from '@bingads-webui/filter-schema';
import { addCampaignTypePropertyForFilter, baseEntitySchemaForAvailableInsightTypeIds, lambdaConvertForAvailableInsightTypeIds } from '@bingads-webui-campaign/common-grid-config';
import { getInsightsMetricFilterOption, isInsightsMetric } from './metrics';

const entitySchemaCopy = {};

function getflattenEntitySchema(schemaName, permissions) {
  let entitySchema = entitySchemaCopy[schemaName];

  if (entitySchema) {
    return entitySchema;
  }

  switch (schemaName) {
    case 'SearchTerm':
      entitySchema = flattenEntitySchema(SearchTerm);
      addCampaignTypePropertyForFilter({ entitySchema, permissions });
      break;
    case 'Keyword':
      entitySchema = flattenEntitySchema(Keyword);
      addCampaignTypePropertyForFilter({ entitySchema, permissions });
      break;
    case 'Campaign':
      entitySchema = flattenEntitySchema(Campaign);
      entitySchema.properties.AvailableInsightTypeIds =
          baseEntitySchemaForAvailableInsightTypeIds;
      break;
    case 'AdGroup':
      entitySchema = flattenEntitySchema(AdGroup);
      addCampaignTypePropertyForFilter({ entitySchema, permissions });
      break;
    case 'AssetGroup':
      entitySchema = flattenEntitySchema(AssetGroup);
      addCampaignTypePropertyForFilter({ entitySchema, permissions });
      break;
    default:
      entitySchema = null;
      break;
  }

  entitySchemaCopy[schemaName] = entitySchema;

  return entitySchema;
}

export function getMergedFilter({
  filter,
  typeName,
  metric,
  isFilterByInsights,
  permissions,
}) {
  if (!filter) {
    return undefined;
  }

  const filters = _.chain(filter)
    .compact()
    .map(({
      filter: {
        key,
        value: { query } = {},
      } = {},
    }) => {
      if (!query) {
        return null;
      }

      let updatedQuery = query;

      if (typeName === 'Campaign') {
        if (key === WaterfallFilterType.AdGroupStatus) {
          return null;
        }

        if (key === WaterfallFilterType.CampaignStatus) {
          updatedQuery = {
            Status: query.CampaignStatus,
          };
        }
      } else if (typeName === 'AdGroup' && (key === WaterfallFilterType.AdGroupStatus)) {
        updatedQuery = {
          Status: query.AdGroupStatus,
        };
      } else if (typeName === 'AssetGroup') {
        // No filters support for asset groups
        return null;
      }

      return new FilterEntity(
        updatedQuery,
        {
          entitySchema: getflattenEntitySchema(typeName, permissions),
        }
      );
    })
    .compact()
    .value();

  if (typeName === 'Campaign'
    && isFilterByInsights
    && isInsightsMetric(metric)) {
    const query = {
      AvailableInsightTypeIds: {
        $contains: [getInsightsMetricFilterOption(metric)],
      },
    };
    const entitySchema = getflattenEntitySchema(typeName, permissions);
    const availableInsightsTypeIdFilter = new FilterEntity(
      query,
      {
        entitySchema,
        odata: {
          fieldMap: buildNameToPropertyMapping(entitySchema),
          ignoreCase: false,
          custom: {
            AvailableInsightTypeIds: {
              contains: lambdaConvertForAvailableInsightTypeIds(),
            },
          },
        },
      }
    );
    filters.push(availableInsightsTypeIdFilter);
  }

  if (!filters || filters.length === 0) {
    return undefined;
  }

  const entitySchema = getflattenEntitySchema(typeName, permissions);
  const mergedFilter = new FilterEntity(
    FilterEntity.merge(filters),
    {
      entitySchema,
      odata: {
        fieldMap: buildNameToPropertyMapping(entitySchema),
        ignoreCase: false,
        custom: {
          AvailableInsightTypeIds: {
            contains: lambdaConvertForAvailableInsightTypeIds(),
          },
        },
      },
    }
  );
  mergedFilter.options = mergedFilter.odataOptions;
  return mergedFilter;
}

export function getConversionGoalsFilterStore({ preferenceService }) {
  const conversionGoalsFilter = new FilterStorageFactory({
    preferenceService,
    featureName: 'ConversionGoalsGrid',
  });

  return conversionGoalsFilter.create('editableFilter', {
    filterEntityOptions: {
      entitySchema: { name: 'Goal' },
    },
  });
}
